/**
 * useToast — convenience hook to consume ToastContext.
 *
 * Extracted into its own file so that react-refresh/only-export-components
 * is satisfied: ToastContext.tsx exports only the ToastProvider component,
 * while this file exports only the hook.
 *
 * Usage:
 *   import { useToast } from '../../app/provider/useToast';
 *   const { showSuccess, showError } = useToast();
 */
import { useContext } from 'react';

import ToastContext from './ToastContext';

interface ToastContextType {
  showSuccess: (title: string, message?: string) => number | string | null;
  showError: (title: string, message?: string) => number | string | null;
  addToast: (title: string, message?: string, duration?: number, type?: string) => number | string | null;
  removeToast: (id: number | string) => void;
}

/**
 * Convenience hook to consume ToastContext.
 * Throws if called outside <ToastProvider>.
 */
export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
